import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import type { RouteRecord } from "vite-react-ssg";
import TaohMathis from "./pages/TaohMathis";
import { Helmet } from "react-helmet-async";
import { TrackingParams } from "@/lib/tracking-params";
import { FunnelClarity } from "@/lib/funnel-clarity";
import { caseStudies } from "@/data/caseStudies";
import { FloatingAiChat } from '@/components/FloatingAiChat';

const queryClient = new QueryClient();

const ScrollToHash = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

/** Initial PageView fires from the head snippet; this only adds one per client-side route change. */
const MetaPixelPageView = () => {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const w = window as unknown as { fbq?: (...a: unknown[]) => void };
    w.fbq?.('track', 'PageView');
  }, [pathname]);
  return null;
};

// HelmetProvider is supplied by vite-react-ssg (client + build-time render).
const Layout = () => {
  const { pathname } = useLocation();
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ScrollToHash />
      <TrackingParams />
      <FunnelClarity />
      <MetaPixelPageView />
      <Outlet />
      {pathname.replace(/\/+$/, '').toLowerCase() !== '/book' && <FloatingAiChat />}
    </TooltipProvider>
  </QueryClientProvider>
  );
};

const page = (load: () => Promise<{ default: React.ComponentType<any> }>) => async () => ({
  Component: (await load()).default,
});

export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <TaohMathis /> },
      { path: "/taohmathis", element: (
        <>
          <TaohMathis />
          {/* Private duplicate of the homepage: own title/canonical, kept out of search. */}
          <Helmet>
            <title>Taoh Mathis | The Alpha Omega Hub</title>
            <meta name="robots" content="noindex, nofollow" />
            <link rel="canonical" href="https://thealphaomegahub.com/taohmathis" />
          </Helmet>
        </>
      ) },
      { path: "/services/video-shooting", lazy: page(() => import("./pages/VideoShooting")) },
      { path: "/services/website-creation", lazy: page(() => import("./pages/WebsiteCreation")) },
      { path: "/services/ads-management", lazy: page(() => import("./pages/AdsManagement")) },
      { path: "/blog", lazy: page(() => import("./pages/Blog")) },
      {
        path: "/blog/:slug",
        lazy: page(() => import("./pages/BlogArticle")),
        getStaticPaths: async () =>
          (await import("./pages/BlogArticle")).articleSlugs().map((s) => `/blog/${s}`),
      },
      { path: "/results", element: <Navigate to="/case-studies" replace /> },
      { path: "/case-studies", lazy: page(() => import("./pages/CaseStudies")) },
      {
        path: "/case-studies/:slug",
        lazy: page(() => import("./pages/CaseStudyDetail")),
        getStaticPaths: () => caseStudies.map((c) => `/case-studies/${c.slug}`),
      },
      { path: "/aa", lazy: page(() => import("./pages/ArnoCard")) },
      { path: "/kj", lazy: page(() => import("./pages/KerimCard")) },
      { path: "/terms", lazy: page(() => import("./pages/TermsOfService")) },
      { path: "/full", lazy: page(() => import("./pages/IndexFull")) },
      { path: "/welcome", lazy: page(() => import("./pages/Welcome")) },
      { path: "/vsl", lazy: page(() => import("./pages/Vsl")) },
      { path: "/vsl-a", lazy: page(() => import("./pages/VslRedirect")) },
      { path: "/vsl-b", lazy: page(() => import("./pages/VslRedirect")) },
      { path: "/content", lazy: page(() => import("./pages/ContentOffer")) },
      { path: "/booked", lazy: page(() => import("./pages/Booked")) },
      { path: "/filled", lazy: page(() => import("./pages/Filled")) },
      { path: "/book", lazy: page(() => import("./pages/Book")) },
      { path: "/start", lazy: page(() => import("./pages/Start")) },
      { path: "/contract", lazy: page(() => import("./pages/Contract")) },
      { path: "/simulator", lazy: page(() => import("./pages/Simulator")) },
      { path: "/scheduler", lazy: page(() => import("./pages/SchedulerRedirect")) },
      { path: "/resources", lazy: page(() => import("./pages/Resources")) },
      { path: "/lm/appointment-setting", lazy: page(() => import("./pages/LmAppointmentSetting")) },
      { path: "/lm/posting", lazy: page(() => import("./pages/LmPosting")) },
      { path: "/lm/script-builder", lazy: page(() => import("./pages/LmScriptBuilder")) },
      { path: "/lm/ad-script-builder", lazy: page(() => import("./pages/LmAdScriptBuilder")) },
      { path: "/lm/video-ad", lazy: page(() => import("./pages/LmVideoAd")) },
      { path: "/script/:id", lazy: page(() => import("./pages/ScriptView")) },
      { path: "/privacy", lazy: page(() => import("./pages/PrivacyPolicy")) },
      { path: "*", lazy: page(() => import("./pages/NotFound")) },
    ],
  },
];
